<template>
  <transition name="help-drawer">
    <div
      v-if="visible"
      class="help-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-title"
      @mousedown.self="close"
    >
      <section class="help-panel">
        <header class="help-header">
          <div class="help-header__title">
            <span class="help-header__icon"><CIcon name="cil-lightbulb"/></span>
            <span>
              <small>実際の画面を見ながら確認</small>
              <strong id="help-title">ヘルプ・操作ガイド</strong>
            </span>
          </div>
          <button
            ref="closeButton"
            type="button"
            class="help-close"
            aria-label="ヘルプを閉じる"
            @click="close"
          >
            <CIcon name="cil-x-circle"/>
          </button>
        </header>

        <div class="help-context">
          <div>
            <small>現在の画面</small>
            <strong>{{ currentPageLabel || '画面名を取得できませんでした' }}</strong>
          </div>
          <span>{{ authorityLabel }}</span>
        </div>

        <div class="help-layout">
          <aside class="help-index">
            <label class="help-search" for="help-search-input">
              <CIcon name="cil-magnifying-glass"/>
              <input
                id="help-search-input"
                v-model.trim="query"
                type="search"
                autocomplete="off"
                placeholder="画面名・項目名・困りごとを検索"
              >
              <button v-if="query" type="button" @click="query = ''">消去</button>
            </label>
            <p class="help-search__hint">例：保存できない、単価、承認取消、支払期日</p>

            <button
              v-if="currentMatch"
              type="button"
              class="help-current-link"
              @click="selectMatch(currentMatch)"
            >
              <CIcon name="cil-location-pin"/>
              <span>
                <small>現在の画面の説明</small>
                <strong>{{ currentMatch.screen.title }}</strong>
              </span>
              <CIcon name="cil-chevron-right"/>
            </button>

            <nav class="help-guide-list" aria-label="操作ガイド一覧">
              <section v-for="group in groupedGuides" :key="group.scope">
                <h2>
                  <span>{{ group.scope }}</span>
                  <small>{{ group.guides.length }}件</small>
                </h2>
                <button
                  v-for="guide in group.guides"
                  :key="guide.id"
                  type="button"
                  :class="{ 'is-active': activeGuideId === guide.id }"
                  @click="selectGuide(guide.id)"
                >
                  <CIcon :name="guide.icon"/>
                  <span>
                    <strong>{{ guide.title }}</strong>
                    <small>{{ guide.screens.length }}画面</small>
                  </span>
                  <CIcon name="cil-chevron-right"/>
                </button>
              </section>
            </nav>

            <p v-if="query && filteredGuides.length" class="help-search__count">
              {{ filteredGuides.length }}件のガイドが見つかりました
            </p>
          </aside>

          <main v-if="activeGuide && activeScreen" ref="content" class="help-content" tabindex="-1">
            <header class="guide-heading">
              <div>
                <span class="guide-scope">{{ activeGuide.scope }}</span>
                <h2>{{ activeGuide.title }}</h2>
                <p>{{ activeGuide.summary }}</p>
              </div>
              <button
                v-if="activeScreen.route && !isActiveScreenCurrent"
                type="button"
                class="guide-open-button"
                @click="openRoute(activeScreen.route)"
              >
                この画面を開く
                <CIcon name="cil-arrow-right"/>
              </button>
            </header>

            <nav
              v-if="activeGuide.screens.length > 1"
              class="screen-tabs"
              role="tablist"
              :aria-label="activeGuide.title + ' の関連画面'"
            >
              <button
                v-for="screen in activeGuide.screens"
                :key="screen.id"
                type="button"
                role="tab"
                :aria-selected="activeScreenId === screen.id ? 'true' : 'false'"
                :class="{ 'is-active': activeScreenId === screen.id }"
                @click="selectScreen(screen.id)"
              >
                {{ screen.tab }}
                <span v-if="isScreenCurrent(screen)">現在</span>
              </button>
            </nav>

            <article :key="activeScreen.id" class="screen-help">
              <header class="screen-heading">
                <div>
                  <span v-if="isActiveScreenCurrent" class="current-badge">
                    <CIcon name="cil-location-pin"/>
                    現在表示中
                  </span>
                  <h3>{{ activeScreen.title }}</h3>
                  <p>{{ activeScreen.purpose }}</p>
                </div>
              </header>

              <section class="entry-card">
                <span><CIcon name="cil-cursor"/></span>
                <div>
                  <small>この画面の開き方</small>
                  <p>{{ activeScreen.entry }}</p>
                </div>
              </section>

              <section v-if="isActiveScreenCurrent && liveUiGroups.length" class="live-ui">
                <div class="section-title">
                  <span><CIcon name="cil-laptop"/></span>
                  <div>
                    <small>現在表示されている実画面から取得</small>
                    <strong>画面上の見出し・項目・操作</strong>
                  </div>
                </div>
                <div class="ui-groups">
                  <div v-for="group in liveUiGroups" :key="group.label" class="ui-group ui-group--live">
                    <h4>{{ group.label }}</h4>
                    <ul>
                      <li v-for="item in group.items" :key="item">{{ item }}</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section class="source-ui">
                <div class="section-title">
                  <span><CIcon name="cil-list-rich"/></span>
                  <div>
                    <small>実装ソースのテンプレートから整理</small>
                    <strong>この画面にある項目・ボタン</strong>
                  </div>
                </div>
                <div class="ui-groups">
                  <div v-for="group in activeScreen.uiGroups" :key="group.label" class="ui-group">
                    <h4>{{ group.label }}</h4>
                    <ul>
                      <li v-for="item in group.items" :key="item">{{ item }}</li>
                    </ul>
                  </div>
                </div>
                <p class="source-ui__note">
                  状態・権限・登録データによって、表示されない項目や無効になるボタンがあります。
                </p>
              </section>

              <section class="steps-section">
                <div class="section-title">
                  <span><CIcon name="cil-check-circle"/></span>
                  <div>
                    <small>画面の項目名とボタン名に合わせた手順</small>
                    <strong>操作の流れ</strong>
                  </div>
                </div>
                <ol class="steps-list">
                  <li v-for="(step, index) in activeScreen.steps" :key="step.title">
                    <span>{{ index + 1 }}</span>
                    <div>
                      <h4>{{ step.title }}</h4>
                      <p>{{ step.body }}</p>
                      <div v-if="step.confirm" class="step-confirm">
                        <CIcon name="cil-check"/>
                        <span><strong>確認:</strong> {{ step.confirm }}</span>
                      </div>
                    </div>
                  </li>
                </ol>
              </section>

              <section v-if="activeScreen.notes && activeScreen.notes.length" class="notes-section">
                <div class="section-title">
                  <span><CIcon name="cil-lightbulb"/></span>
                  <div>
                    <small>操作前に確認</small>
                    <strong>注意点</strong>
                  </div>
                </div>
                <ul>
                  <li v-for="note in activeScreen.notes" :key="note">{{ note }}</li>
                </ul>
              </section>

              <section v-if="activeScreen.problems && activeScreen.problems.length" class="problems-section">
                <div class="section-title section-title--warning">
                  <span><CIcon name="cil-warning"/></span>
                  <div>
                    <small>同じ操作を繰り返す前に確認</small>
                    <strong>困ったとき</strong>
                  </div>
                </div>
                <details v-for="problem in activeScreen.problems" :key="problem.title">
                  <summary>
                    <span>{{ problem.title }}</span>
                    <CIcon name="cil-chevron-bottom"/>
                  </summary>
                  <div class="problem-body">
                    <div>
                      <strong>まず確認</strong>
                      <ul>
                        <li v-for="check in problem.checks" :key="check">{{ check }}</li>
                      </ul>
                    </div>
                    <div>
                      <strong>対応</strong>
                      <ol>
                        <li v-for="action in problem.actions" :key="action">{{ action }}</li>
                      </ol>
                    </div>
                  </div>
                </details>
              </section>

              <section v-if="activeGuide.screens.length > 1" class="related-section">
                <div class="section-title">
                  <span><CIcon name="cil-layers"/></span>
                  <div>
                    <small>同じ機能の関連画面</small>
                    <strong>一覧・詳細・登録・更新</strong>
                  </div>
                </div>
                <div>
                  <button
                    v-for="screen in activeGuide.screens"
                    :key="screen.id"
                    type="button"
                    :class="{ 'is-active': screen.id === activeScreenId }"
                    @click="selectScreen(screen.id)"
                  >
                    {{ screen.title }}
                    <CIcon name="cil-chevron-right"/>
                  </button>
                </div>
              </section>

              <section class="support-section">
                <div>
                  <span><CIcon name="cil-notes"/></span>
                  <div>
                    <small>解決しない場合</small>
                    <strong>問い合わせ用の情報をまとめる</strong>
                  </div>
                </div>
                <p>発生日時、画面名、対象年月・データ、行った操作、表示文言、期待した結果を記録してください。</p>
                <button type="button" @click="copySupportMemo">
                  <CIcon name="cil-description"/>
                  {{ copied ? 'コピーしました' : '問い合わせ用メモをコピー' }}
                </button>
                <small>パスワード、Slack Token、不要な個人情報は記載しないでください。</small>
              </section>

              <footer class="help-footer">
                <button
                  v-if="activeScreen.route && !isActiveScreenCurrent"
                  type="button"
                  class="help-footer__open"
                  @click="openRoute(activeScreen.route)"
                >
                  {{ activeScreen.title }}を開く
                  <CIcon name="cil-arrow-right"/>
                </button>
                <button type="button" class="help-footer__close" @click="close">
                  ヘルプを閉じる
                </button>
              </footer>
            </article>
          </main>

          <main v-else class="help-empty" aria-live="polite">
            <CIcon name="cil-magnifying-glass"/>
            <h2>該当する説明が見つかりません</h2>
            <p>画面上の項目名やボタン名を短く入力してください。</p>
            <button type="button" @click="query = ''">すべてのガイドを表示</button>
          </main>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import helpGuides from './helpContent'

export default {
  name: 'HelpCenter',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    authority: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      guides: helpGuides,
      query: '',
      activeGuideId: '',
      activeScreenId: '',
      liveUiGroups: [],
      previousBodyOverflow: '',
      previouslyFocusedElement: null,
      pageLocked: false,
      copied: false,
      copyTimerId: null
    }
  },

  computed: {
    normalizedAuthority () {
      const value = String(this.authority || '').trim().toLowerCase()
      if (value === 'admin' || value === 'clerk') return value
      return 'common'
    },

    authorityLabel () {
      return {
        common: '一般ユーザー',
        clerk: '事務権限',
        admin: '管理者権限'
      }[this.normalizedAuthority]
    },

    availableGuides () {
      return this.guides.filter(guide => {
        return !guide.authorities || guide.authorities.includes(this.normalizedAuthority)
      })
    },

    filteredGuides () {
      const terms = this.normalize(this.query).split(/\s+/).filter(Boolean)
      if (!terms.length) return this.availableGuides

      return this.availableGuides.filter(guide => {
        return terms.every(term => this.guideSearchText(guide).includes(term))
      })
    },

    groupedGuides () {
      const order = ['共通', '事務・管理', '管理者']
      return order
        .map(scope => ({
          scope,
          guides: this.filteredGuides.filter(guide => guide.scope === scope)
        }))
        .filter(group => group.guides.length)
    },

    activeGuide () {
      return this.availableGuides.find(guide => guide.id === this.activeGuideId) || null
    },

    activeScreen () {
      if (!this.activeGuide) return null
      return this.activeGuide.screens.find(screen => screen.id === this.activeScreenId) ||
        this.activeGuide.screens[0] ||
        null
    },

    currentPath () {
      const routePath = this.$route && this.$route.path ? this.$route.path : ''
      return this.normalizePath(routePath)
    },

    currentMatch () {
      let best = null

      this.availableGuides.forEach(guide => {
        guide.screens.forEach(screen => {
          const score = this.screenMatchScore(screen, this.currentPath)
          if (score < 0) return
          if (!best || score > best.score) {
            best = { guide, screen, score }
          }
        })
      })

      return best
    },

    currentPageLabel () {
      if (this.$route && this.$route.name) return this.$route.name
      if (this.currentMatch) return this.currentMatch.screen.title
      return ''
    },

    isActiveScreenCurrent () {
      return this.activeScreen ? this.isScreenCurrent(this.activeScreen) : false
    },

    supportMemo () {
      const route = this.$route && this.$route.fullPath ? this.$route.fullPath : ''
      return [
        '【発生日時】',
        '【画面名】 ' + (this.currentPageLabel || ''),
        '【ヘルプ項目】 ' + (this.activeScreen ? this.activeScreen.title : ''),
        '【対象年月・対象データ】',
        '【行った操作】',
        '【表示された文言】',
        '【期待した結果】',
        route ? '【画面URL】 ' + route : ''
      ].filter(Boolean).join('\n')
    }
  },

  watch: {
    visible (value) {
      if (value) this.open()
      else this.restorePage()
    },

    query () {
      this.syncFilteredSelection()
    },

    '$route.fullPath' () {
      if (!this.visible) return
      this.$nextTick(() => {
        this.syncCurrentRoute()
        this.collectLiveUi()
      })
    }
  },

  beforeDestroy () {
    this.restorePage()
    if (this.copyTimerId) window.clearTimeout(this.copyTimerId)
  },

  methods: {
    normalize (value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\u3000/g, ' ')
        .replace(/[＋+]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },

    normalizePath (value) {
      const path = String(value || '').split('?')[0].split('#')[0]
      const normalized = path.replace(/\/+/g, '/').replace(/\/$/, '')
      return (normalized || '/').toLowerCase()
    },

    patternMatches (pattern, path) {
      const patternParts = this.normalizePath(pattern).split('/').filter(Boolean)
      const pathParts = this.normalizePath(path).split('/').filter(Boolean)
      if (patternParts.length !== pathParts.length) return false

      return patternParts.every((part, index) => {
        if (part.charAt(0) === ':') return Boolean(pathParts[index])
        return part === pathParts[index]
      })
    },

    screenMatchScore (screen, path) {
      let best = -1
      ;(screen.pathPatterns || []).forEach(pattern => {
        if (!this.patternMatches(pattern, path)) return
        const literals = this.normalizePath(pattern)
          .split('/')
          .filter(part => part && part.charAt(0) !== ':')
          .length
        best = Math.max(best, literals * 100 + pattern.length)
      })
      return best
    },

    isScreenCurrent (screen) {
      return this.screenMatchScore(screen, this.currentPath) >= 0
    },

    guideSearchText (guide) {
      const values = [
        guide.title,
        guide.scope,
        guide.summary
      ].concat(guide.keywords || [])

      guide.screens.forEach(screen => {
        values.push(screen.tab, screen.title, screen.entry, screen.purpose, screen.sourceFile)

        ;(screen.uiGroups || []).forEach(group => {
          values.push(group.label)
          values.push.apply(values, group.items || [])
        })

        ;(screen.steps || []).forEach(step => {
          values.push(step.title, step.body, step.confirm)
        })

        values.push.apply(values, screen.notes || [])

        ;(screen.problems || []).forEach(problem => {
          values.push(problem.title)
          values.push.apply(values, problem.checks || [])
          values.push.apply(values, problem.actions || [])
        })
      })

      return this.normalize(values.filter(Boolean).join(' '))
    },

    matchingScreenForQuery (guide) {
      const terms = this.normalize(this.query).split(/\s+/).filter(Boolean)
      if (!terms.length) return null

      return guide.screens.find(screen => {
        const values = [
          screen.tab,
          screen.title,
          screen.entry,
          screen.purpose,
          screen.sourceFile
        ]

        ;(screen.uiGroups || []).forEach(group => {
          values.push(group.label)
          values.push.apply(values, group.items || [])
        })

        ;(screen.steps || []).forEach(step => {
          values.push(step.title, step.body, step.confirm)
        })

        values.push.apply(values, screen.notes || [])

        ;(screen.problems || []).forEach(problem => {
          values.push(problem.title)
          values.push.apply(values, problem.checks || [])
          values.push.apply(values, problem.actions || [])
        })

        const target = this.normalize(values.filter(Boolean).join(' '))
        return terms.every(term => target.includes(term))
      }) || null
    },

    syncFilteredSelection () {
      if (!this.filteredGuides.length) {
        this.activeGuideId = ''
        this.activeScreenId = ''
        return
      }

      const selected = this.filteredGuides.find(guide => guide.id === this.activeGuideId)
      const guide = selected || this.filteredGuides[0]
      const queryScreen = this.matchingScreenForQuery(guide)

      this.activeGuideId = guide.id
      if (!guide.screens.some(screen => screen.id === this.activeScreenId) || queryScreen) {
        this.activeScreenId = queryScreen ? queryScreen.id : guide.screens[0].id
      }

      this.scrollContentTop()
    },

    syncCurrentRoute () {
      if (this.currentMatch) {
        this.activeGuideId = this.currentMatch.guide.id
        this.activeScreenId = this.currentMatch.screen.id
        return
      }

      if (!this.activeGuideId && this.availableGuides.length) {
        this.activeGuideId = this.availableGuides[0].id
        this.activeScreenId = this.availableGuides[0].screens[0].id
      }
    },

    selectMatch (match) {
      this.query = ''
      this.activeGuideId = match.guide.id
      this.activeScreenId = match.screen.id
      this.scrollContentTop()
    },

    selectGuide (guideId) {
      const guide = this.availableGuides.find(item => item.id === guideId)
      if (!guide) return

      this.activeGuideId = guide.id
      const currentScreen = guide.screens.find(screen => this.isScreenCurrent(screen))
      const queryScreen = this.matchingScreenForQuery(guide)
      this.activeScreenId = (currentScreen || queryScreen || guide.screens[0]).id
      this.scrollContentTop()
    },

    selectScreen (screenId) {
      if (!this.activeGuide) return
      if (!this.activeGuide.screens.some(screen => screen.id === screenId)) return
      this.activeScreenId = screenId
      this.scrollContentTop()
    },

    scrollContentTop () {
      this.$nextTick(() => {
        if (this.$refs.content) this.$refs.content.scrollTop = 0
      })
    },

    open () {
      if (!this.pageLocked) {
        this.pageLocked = true
        this.previouslyFocusedElement = document.activeElement
        this.previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', this.handleKeydown)
      }

      this.query = ''
      this.syncCurrentRoute()
      this.$nextTick(() => {
        this.collectLiveUi()
        if (this.$refs.closeButton) this.$refs.closeButton.focus()
      })
    },

    close () {
      this.$emit('close')
    },

    restorePage () {
      if (!this.pageLocked) return

      this.pageLocked = false
      document.body.style.overflow = this.previousBodyOverflow
      document.removeEventListener('keydown', this.handleKeydown)

      if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
        this.previouslyFocusedElement.focus()
      }
      this.previouslyFocusedElement = null
    },

    handleKeydown (event) {
      if (event.key === 'Escape') this.close()
    },

    collectLiveUi () {
      const root = document.querySelector('.c-main')
      if (!root) {
        this.liveUiGroups = []
        return
      }

      const collect = (selector, limit) => {
        const values = []
        root.querySelectorAll(selector).forEach(element => {
          if (values.length >= limit) return
          if (!this.isVisibleElement(element)) return

          const text = this.cleanElementText(element)
          if (!text || values.includes(text)) return
          values.push(text)
        })
        return values
      }

      const groups = [
        { label: '見出し', items: collect('h1, h2, h3, .card-title, .card-header strong', 16) },
        { label: '項目・列名', items: collect('th, label, legend', 32) },
        { label: 'ボタン・リンク', items: collect('button, a.btn, .card-header-actions a', 24) }
      ].filter(group => group.items.length)

      this.liveUiGroups = groups
    },

    isVisibleElement (element) {
      if (!element || !element.getBoundingClientRect) return false
      const style = window.getComputedStyle(element)
      if (style.display === 'none' || style.visibility === 'hidden') return false
      const rect = element.getBoundingClientRect()
      return rect.width > 0 && rect.height > 0
    },

    cleanElementText (element) {
      const clone = element.cloneNode(true)
      clone.querySelectorAll('.sr-only, svg, .c-icon').forEach(child => child.remove())
      const text = this.normalizeDisplayText(clone.innerText || clone.textContent || '')
      if (!text || text.length > 80) return ''
      return text
    },

    normalizeDisplayText (value) {
      return String(value || '')
        .replace(/\s+/g, ' ')
        .replace(/^\s+|\s+$/g, '')
    },

    openRoute (to) {
      this.close()
      if (to && this.$route.fullPath !== to) {
        this.$router.push(to).catch(() => {})
      }
    },

    copySupportMemo () {
      const finish = () => {
        this.copied = true
        if (this.copyTimerId) window.clearTimeout(this.copyTimerId)
        this.copyTimerId = window.setTimeout(() => {
          this.copied = false
        }, 2200)
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(this.supportMemo)
          .then(finish)
          .catch(() => this.fallbackCopy(finish))
        return
      }

      this.fallbackCopy(finish)
    },

    fallbackCopy (finish) {
      const textarea = document.createElement('textarea')
      textarea.value = this.supportMemo
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      finish()
    }
  }
}
</script>

<style scoped>
.help-overlay {
  position: fixed;
  z-index: 2200;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(5, 20, 31, .22);
}

.help-panel {
  display: flex;
  width: min(880px, calc(100vw - 72px));
  height: 100vh;
  overflow: hidden;
  flex-direction: column;
  background: #f4f7f9;
  box-shadow: -24px 0 70px rgba(4, 24, 38, .25);
}

.help-header {
  display: flex;
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  padding: 11px 16px;
  background: #0b2d48;
  color: #fff;
}

.help-header__title {
  display: flex;
  align-items: center;
  gap: 11px;
}

.help-header__icon {
  display: inline-flex;
  width: 41px;
  height: 41px;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: rgba(255, 255, 255, .12);
}

.help-header__title small,
.help-header__title strong {
  display: block;
}

.help-header__title small {
  color: #a9c3d5;
  font-size: 9px;
}

.help-header__title strong {
  margin-top: 2px;
  font-size: 17px;
}

.help-close {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, .2);
  border-radius: 10px;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.help-close:hover,
.help-close:focus {
  outline: none;
  background: rgba(255, 255, 255, .12);
}

.help-context {
  display: flex;
  min-height: 47px;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #d7e0e6;
  background: #fff;
  gap: 12px;
}

.help-context small,
.help-context strong {
  display: block;
}

.help-context small {
  color: #7d8d98;
  font-size: 8px;
}

.help-context strong {
  max-width: 620px;
  overflow: hidden;
  margin-top: 2px;
  color: #29475b;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.help-context > span {
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 999px;
  background: #e6f2f9;
  color: #0872b6;
  font-size: 8px;
  font-weight: 800;
}

.help-layout {
  display: grid;
  min-height: 0;
  flex: 1;
  grid-template-columns: 250px minmax(0, 1fr);
}

.help-index {
  overflow-y: auto;
  padding: 13px 11px 18px;
  border-right: 1px solid #d6e0e6;
  background: #eaf0f4;
}

.help-search {
  display: flex;
  min-height: 42px;
  align-items: center;
  padding: 0 9px;
  border: 1px solid #cbd7df;
  border-radius: 9px;
  background: #fff;
  color: #647d8d;
  gap: 7px;
}

.help-search:focus-within {
  border-color: #177bb9;
  box-shadow: 0 0 0 3px rgba(23, 123, 185, .12);
}

.help-search input {
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: #264459;
  font-size: 10px;
}

.help-search button {
  border: 0;
  background: transparent;
  color: #0872b6;
  cursor: pointer;
  font-size: 8px;
  font-weight: 800;
}

.help-search__hint {
  margin: 6px 3px 10px;
  color: #758894;
  font-size: 8px;
  line-height: 1.5;
}

.help-current-link {
  display: grid;
  width: 100%;
  min-height: 48px;
  align-items: center;
  margin-bottom: 12px;
  padding: 7px 9px;
  border: 1px solid #bbd8e9;
  border-radius: 9px;
  background: #f5fbff;
  color: #176f9f;
  cursor: pointer;
  grid-template-columns: 19px minmax(0, 1fr) 13px;
  gap: 7px;
  text-align: left;
}

.help-current-link small,
.help-current-link strong {
  display: block;
}

.help-current-link small {
  font-size: 7px;
}

.help-current-link strong {
  overflow: hidden;
  margin-top: 2px;
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.help-guide-list {
  display: grid;
  gap: 13px;
}

.help-guide-list section h2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 5px 5px;
  color: #677c8a;
  font-size: 8px;
  letter-spacing: .08em;
}

.help-guide-list section h2 small {
  font-size: 7px;
  font-weight: 500;
  letter-spacing: 0;
}

.help-guide-list section > button {
  display: grid;
  width: 100%;
  min-height: 42px;
  align-items: center;
  margin-bottom: 4px;
  padding: 7px 8px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #425d6e;
  cursor: pointer;
  grid-template-columns: 19px minmax(0, 1fr) 13px;
  gap: 6px;
  text-align: left;
}

.help-guide-list section > button:hover,
.help-guide-list section > button:focus {
  outline: none;
  background: rgba(255, 255, 255, .75);
}

.help-guide-list section > button.is-active {
  background: #fff;
  box-shadow: 0 5px 15px rgba(27, 57, 77, .09);
  color: #0872b6;
}

.help-guide-list button strong,
.help-guide-list button small {
  display: block;
}

.help-guide-list button strong {
  font-size: 9px;
  line-height: 1.4;
}

.help-guide-list button small {
  margin-top: 2px;
  color: #81919c;
  font-size: 7px;
}

.help-guide-list button > .c-icon:last-child {
  width: 11px;
  height: 11px;
}

.help-search__count {
  margin: 12px 0 0;
  color: #6d818e;
  font-size: 8px;
  text-align: center;
}

.help-content {
  overflow-y: auto;
  padding: 17px;
  outline: none;
}

.guide-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 16px;
  border: 1px solid #d8e1e7;
  border-radius: 13px;
  background: #fff;
  gap: 15px;
}

.guide-scope {
  display: inline-block;
  margin-bottom: 5px;
  color: #0872b6;
  font-size: 8px;
  font-weight: 800;
}

.guide-heading h2 {
  margin: 0 0 5px;
  color: #17364b;
  font-size: 19px;
}

.guide-heading p {
  margin: 0;
  color: #5c7281;
  font-size: 10px;
  line-height: 1.7;
}

.guide-open-button,
.help-footer button,
.support-section button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 800;
  gap: 6px;
}

.guide-open-button {
  flex: 0 0 auto;
  padding: 9px 11px;
  background: #e6f2f9;
  color: #0872b6;
  font-size: 8px;
}

.screen-tabs {
  display: flex;
  overflow-x: auto;
  margin-bottom: 12px;
  padding: 4px;
  border: 1px solid #d7e1e7;
  border-radius: 10px;
  background: #e9eff3;
  gap: 4px;
}

.screen-tabs button {
  display: inline-flex;
  min-width: max-content;
  align-items: center;
  padding: 7px 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #516a79;
  cursor: pointer;
  font-size: 8px;
  font-weight: 800;
  gap: 5px;
}

.screen-tabs button.is-active {
  background: #fff;
  box-shadow: 0 3px 10px rgba(24, 54, 73, .08);
  color: #0872b6;
}

.screen-tabs button span {
  padding: 2px 5px;
  border-radius: 999px;
  background: #dff2e7;
  color: #25794c;
  font-size: 7px;
}

.screen-help > section,
.screen-heading {
  margin-bottom: 12px;
  border: 1px solid #d8e2e8;
  border-radius: 12px;
  background: #fff;
}

.screen-heading {
  padding: 15px 16px;
}

.current-badge {
  display: inline-flex;
  align-items: center;
  margin-bottom: 5px;
  padding: 4px 7px;
  border-radius: 999px;
  background: #e3f4e9;
  color: #287c4e;
  font-size: 7px;
  font-weight: 800;
  gap: 4px;
}

.screen-heading h3 {
  margin: 0 0 5px;
  color: #1e3e53;
  font-size: 16px;
}

.screen-heading p {
  margin: 0;
  color: #5e7482;
  font-size: 10px;
  line-height: 1.7;
}

.entry-card {
  display: flex;
  align-items: flex-start;
  padding: 12px 14px;
  border-color: #cfe3ef !important;
  background: #f4fbff !important;
  gap: 9px;
}

.entry-card > span {
  display: inline-flex;
  width: 31px;
  height: 31px;
  align-items: center;
  justify-content: center;
  flex: 0 0 31px;
  border-radius: 8px;
  background: #deeff9;
  color: #0872b6;
}

.entry-card small,
.entry-card p {
  display: block;
}

.entry-card small {
  color: #257aa8;
  font-size: 8px;
  font-weight: 800;
}

.entry-card p {
  margin: 3px 0 0;
  color: #3e6176;
  font-size: 9px;
  line-height: 1.6;
}

.live-ui,
.source-ui,
.steps-section,
.notes-section,
.problems-section,
.related-section {
  padding: 14px;
}

.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}

.section-title > span {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  border-radius: 8px;
  background: #e7f2f9;
  color: #0872b6;
}

.section-title small,
.section-title strong {
  display: block;
}

.section-title small {
  color: #81919c;
  font-size: 7px;
}

.section-title strong {
  margin-top: 2px;
  color: #29485c;
  font-size: 11px;
}

.section-title--warning > span {
  background: #fff0dc;
  color: #b66b17;
}

.ui-groups {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.ui-group {
  padding: 9px;
  border-radius: 8px;
  background: #f5f8fa;
}

.ui-group--live {
  border: 1px solid #cfe6d7;
  background: #f4fbf6;
}

.ui-group h4 {
  margin: 0 0 6px;
  color: #3a5668;
  font-size: 8px;
}

.ui-group ul {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  gap: 4px;
  list-style: none;
}

.ui-group li {
  padding: 4px 6px;
  border: 1px solid #d5e0e6;
  border-radius: 5px;
  background: #fff;
  color: #4d6878;
  font-size: 7px;
  line-height: 1.4;
}

.ui-group--live li {
  border-color: #cfe3d6;
}

.source-ui__note {
  margin: 9px 0 0;
  color: #7a8c97;
  font-size: 7px;
  line-height: 1.6;
}

.steps-list {
  display: grid;
  margin: 0;
  padding: 0;
  gap: 8px;
  list-style: none;
}

.steps-list > li {
  display: grid;
  padding: 10px;
  border: 1px solid #e0e7eb;
  border-radius: 8px;
  background: #fafcfd;
  grid-template-columns: 27px minmax(0, 1fr);
  gap: 9px;
}

.steps-list > li > span {
  display: inline-flex;
  width: 27px;
  height: 27px;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: #0b5f96;
  color: #fff;
  font-size: 8px;
  font-weight: 900;
}

.steps-list h4 {
  margin: 1px 0 4px;
  color: #2f4f63;
  font-size: 9px;
}

.steps-list p {
  margin: 0;
  color: #5a7180;
  font-size: 8px;
  line-height: 1.7;
}

.step-confirm {
  display: flex;
  align-items: flex-start;
  margin-top: 7px;
  padding: 7px 8px;
  border-radius: 6px;
  background: #eef7f1;
  color: #3e6950;
  font-size: 7px;
  line-height: 1.6;
  gap: 5px;
}

.notes-section > ul {
  margin: 0;
  padding-left: 19px;
  color: #5c7180;
  font-size: 8px;
  line-height: 1.75;
}

.problems-section details {
  margin-top: 6px;
  overflow: hidden;
  border: 1px solid #e0e7eb;
  border-radius: 8px;
}

.problems-section summary {
  display: flex;
  min-height: 40px;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  color: #405b6d;
  cursor: pointer;
  font-size: 9px;
  font-weight: 800;
  list-style: none;
}

.problems-section summary::-webkit-details-marker {
  display: none;
}

.problems-section details[open] summary {
  border-bottom: 1px solid #e2e8ec;
  background: #fff8ed;
  color: #8b5a19;
}

.problems-section summary .c-icon {
  width: 12px;
  transition: transform .15s ease;
}

.problems-section details[open] summary .c-icon {
  transform: rotate(180deg);
}

.problem-body {
  display: grid;
  padding: 10px;
  grid-template-columns: 1fr 1.2fr;
  gap: 8px;
}

.problem-body > div {
  padding: 8px;
  border-radius: 7px;
  background: #f5f8fa;
}

.problem-body strong {
  color: #405d6f;
  font-size: 8px;
}

.problem-body ul,
.problem-body ol {
  margin: 6px 0 0;
  padding-left: 16px;
  color: #5d7381;
  font-size: 8px;
  line-height: 1.7;
}

.related-section > div:last-child {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.related-section button {
  display: inline-flex;
  align-items: center;
  padding: 7px 9px;
  border: 1px solid #d5e1e8;
  border-radius: 7px;
  background: #fff;
  color: #496577;
  cursor: pointer;
  font-size: 8px;
  font-weight: 700;
  gap: 5px;
}

.related-section button.is-active {
  border-color: #77acd0;
  background: #eaf5fb;
  color: #0872b6;
}

.support-section {
  padding: 13px 14px;
  border-left: 4px solid #d3942a !important;
  background: #fff9ef !important;
}

.support-section > div:first-child {
  display: flex;
  align-items: center;
  gap: 8px;
}

.support-section > div:first-child > span {
  display: inline-flex;
  width: 31px;
  height: 31px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #ffedca;
  color: #9b6315;
}

.support-section small,
.support-section strong {
  display: block;
}

.support-section > div small {
  color: #92713e;
  font-size: 7px;
}

.support-section > div strong {
  margin-top: 2px;
  color: #6b4b1d;
  font-size: 10px;
}

.support-section p {
  margin: 8px 0;
  color: #765d36;
  font-size: 8px;
  line-height: 1.7;
}

.support-section button {
  padding: 8px 10px;
  background: #8f641e;
  color: #fff;
  font-size: 8px;
}

.support-section > small:last-child {
  margin-top: 6px;
  color: #8b795d;
  font-size: 7px;
}

.help-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 0 8px;
  gap: 7px;
}

.help-footer__open {
  padding: 9px 12px;
  background: #e6f2f9;
  color: #0872b6;
  font-size: 8px;
}

.help-footer__close {
  padding: 9px 14px;
  background: #263f51;
  color: #fff;
  font-size: 8px;
}

.help-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 30px;
  color: #728794;
  text-align: center;
}

.help-empty > .c-icon {
  width: 34px;
  height: 34px;
  margin-bottom: 12px;
}

.help-empty h2 {
  margin: 0 0 6px;
  color: #29475a;
  font-size: 17px;
}

.help-empty p {
  margin: 0 0 13px;
  font-size: 9px;
}

.help-empty button {
  padding: 8px 11px;
  border: 0;
  border-radius: 7px;
  background: #0872b6;
  color: #fff;
  cursor: pointer;
  font-size: 8px;
  font-weight: 800;
}

.help-drawer-enter-active,
.help-drawer-leave-active {
  transition: opacity .16s ease;
}

.help-drawer-enter-active .help-panel,
.help-drawer-leave-active .help-panel {
  transition: transform .2s ease;
}

.help-drawer-enter,
.help-drawer-leave-to {
  opacity: 0;
}

.help-drawer-enter .help-panel,
.help-drawer-leave-to .help-panel {
  transform: translateX(28px);
}

@media (max-width: 991.98px) {
  .help-panel {
    width: calc(100vw - 36px);
  }

  .help-layout {
    grid-template-columns: 220px minmax(0, 1fr);
  }
}

@media (max-width: 767.98px) {
  .help-panel {
    width: 100vw;
  }

  .help-context {
    padding: 7px 11px;
  }

  .help-layout {
    display: flex;
    overflow: hidden;
    flex-direction: column;
  }

  .help-index {
    flex: 0 0 auto;
    overflow: hidden;
    padding: 8px 9px;
    border-right: 0;
    border-bottom: 1px solid #d6e0e6;
  }

  .help-search {
    min-height: 37px;
  }

  .help-search__hint,
  .help-search__count,
  .help-current-link {
    display: none;
  }

  .help-guide-list {
    display: flex;
    overflow-x: auto;
    margin-top: 7px;
    gap: 7px;
  }

  .help-guide-list section {
    display: flex;
    flex: 0 0 auto;
    gap: 5px;
  }

  .help-guide-list section h2 {
    display: none;
  }

  .help-guide-list section > button {
    width: auto;
    min-width: max-content;
    min-height: 35px;
    margin: 0;
    padding: 6px 8px;
    grid-template-columns: 16px auto;
  }

  .help-guide-list section > button > .c-icon:last-child,
  .help-guide-list button small {
    display: none;
  }

  .help-content {
    padding: 10px;
  }

  .guide-heading {
    display: block;
    padding: 13px;
  }

  .guide-heading h2 {
    font-size: 16px;
  }

  .guide-open-button {
    margin-top: 9px;
  }

  .ui-groups,
  .problem-body {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 420px) {
  .help-header {
    min-height: 61px;
    padding: 9px 10px;
  }

  .help-header__icon {
    width: 37px;
    height: 37px;
  }

  .help-header__title strong {
    font-size: 14px;
  }

  .help-context > span {
    display: none;
  }

  .guide-heading,
  .screen-heading,
  .live-ui,
  .source-ui,
  .steps-section,
  .notes-section,
  .problems-section,
  .related-section {
    padding: 11px;
  }
}
</style>
